// routesConfig.ts
import type React from "react";
import { lazy } from "react";
import Dashboard from "../pages/dashboard/DashboardWrapper";
import PlaceholderPage from "../pages/PlaceholderPage";
import RegistrationFormPage from "../pages/registration/components/RegistrationFormPage";
import RegistrationPage from "../pages/registration/components/RegistrationPage";
import SchemeDataEntryFormPage from "../pages/schemeDataEntry/SchemeDataEntryFormPage";
import SchemeDataEntryList from "../pages/schemeDataEntry/SchemeDataEntryList";

// Define a type for route configuration
interface RouteConfig {
	path: string;
	component: React.ComponentType<any>;
	allowedRoles: number[]; // Array of role_id values that can access this route
}

// Role IDs from screenshot provided by user
const ROLES = {
	ADMIN: 1,
	STATE_OFFICER: 2,
	DRP: 3,
	BRP: 4,
	VRP: 5,
};

// Routes configuration
export const routesConfig: RouteConfig[] = [
	{
		path: "dashboard",
		component: Dashboard,
		allowedRoles: [
			ROLES.ADMIN,
			ROLES.STATE_OFFICER,
			ROLES.DRP,
			ROLES.BRP,
			ROLES.VRP,
		],
	},
	// Registration
	{
		path: "registration",
		component: RegistrationPage,
		allowedRoles: [ROLES.ADMIN, ROLES.STATE_OFFICER, ROLES.DRP, ROLES.BRP],
	},
	{
		path: "registration/add",
		component: RegistrationFormPage,
		allowedRoles: [ROLES.ADMIN, ROLES.STATE_OFFICER, ROLES.DRP, ROLES.BRP],
	},
	{
		path: "registration/add/:id",
		component: RegistrationFormPage,
		allowedRoles: [ROLES.ADMIN, ROLES.STATE_OFFICER, ROLES.DRP, ROLES.BRP],
	},
	{
		path: "registration/edit/:employeeId",
		component: RegistrationFormPage,
		allowedRoles: [ROLES.ADMIN, ROLES.STATE_OFFICER, ROLES.DRP, ROLES.BRP],
	},
	{
		path: "registration/view/:employeeId",
		component: RegistrationFormPage,
		allowedRoles: [ROLES.ADMIN, ROLES.STATE_OFFICER, ROLES.DRP, ROLES.BRP],
	},
	{
		path: "registration/complaint/:employeeId",
		component: lazy(
			() => import("../pages/registration/components/ComplaintFormPage"),
		),
		allowedRoles: [ROLES.ADMIN, ROLES.STATE_OFFICER, ROLES.DRP, ROLES.BRP],
	},
	{
		path: "registration/state-officer",
		component: RegistrationPage,
		allowedRoles: [ROLES.ADMIN, ROLES.STATE_OFFICER],
	},
	{
		path: "registration/state",
		component: RegistrationPage,
		allowedRoles: [ROLES.ADMIN, ROLES.STATE_OFFICER],
	},
	{
		path: "registration/district",
		component: () => (
			<PlaceholderPage title="District level Office Registration" />
		),
		allowedRoles: [ROLES.ADMIN, ROLES.STATE_OFFICER],
	},
	{
		path: "registration/block",
		component: () => (
			<PlaceholderPage title="Block Level Office Registration" />
		),
		allowedRoles: [ROLES.ADMIN, ROLES.STATE_OFFICER],
	},
	{
		path: "master/schemes/*",
		component: lazy(() => import("../pages/scheme/index")),
		allowedRoles: [ROLES.ADMIN],
	},
	{
		path: "master/event-type/*",
		component: lazy(() => import("../pages/eventType/index")),
		allowedRoles: [ROLES.ADMIN],
	},
	{
		path: "master/district/*",
		component: lazy(() => import("../pages/district/index")),
		allowedRoles: [ROLES.ADMIN],
	},
	{
		path: "master/institute/*",
		component: lazy(() => import("../pages/institute/index")),
		allowedRoles: [ROLES.ADMIN],
	},
	{
		path: "master/block/*",
		component: lazy(() => import("../pages/block/index")),
		allowedRoles: [ROLES.ADMIN],
	},
	{
		path: "master/village/*",
		component: lazy(() => import("../pages/village/index")),
		allowedRoles: [ROLES.ADMIN],
	},
	{
		path: "master/school/*",
		component: lazy(() => import("../pages/school/index")),
		allowedRoles: [ROLES.ADMIN],
	},
	{
		path: "master/issue-type/*",
		component: lazy(() => import("../pages/issueType/index")),
		allowedRoles: [ROLES.ADMIN],
	},
	{
		path: "master/issue-category/*",
		component: lazy(() => import("../pages/issueCategory/index")),
		allowedRoles: [ROLES.ADMIN],
	},
	{
		path: "master/issue-sub-category/*",
		component: lazy(() => import("../pages/issueSubCategory/index")),
		allowedRoles: [ROLES.ADMIN],
	},
	{
		path: "master/scheme-issue-mapping/*",
		component: lazy(() => import("../pages/schemeIssueMapping/index")),
		allowedRoles: [ROLES.ADMIN],
	},
	{
		path: "master/classification/*",
		component: lazy(() => import("../pages/classification/index")),
		allowedRoles: [ROLES.ADMIN],
	},
	{
		path: "master/level/*",
		component: lazy(() => import("../pages/level/index")),
		allowedRoles: [ROLES.ADMIN],
	},
	{
		path: "master/course/*",
		component: lazy(() => import("../pages/course/index")),
		allowedRoles: [ROLES.ADMIN],
	},
	{
		path: "master/calendar/*",
		component: lazy(() => import("../pages/auditCalendar/index")),
		allowedRoles: [ROLES.ADMIN, ROLES.DRP, 6 /* DRP I/C */],
	},
	// Scheme Data Entry
	{
		path: "scheme-data-entry/add",
		component: SchemeDataEntryFormPage,
		allowedRoles: [ROLES.BRP],
	},
	{
		path: "scheme-data-entry/edit/:id",
		component: SchemeDataEntryFormPage,
		allowedRoles: [ROLES.ADMIN, ROLES.BRP],
	},
	{
		path: "scheme-data-entry/view/:id",
		component: SchemeDataEntryFormPage,
		allowedRoles: [ROLES.ADMIN, ROLES.STATE_OFFICER, ROLES.DRP, ROLES.BRP],
	},
	{
		path: "scheme-data-entry",
		component: SchemeDataEntryList,
		allowedRoles: [ROLES.ADMIN, ROLES.STATE_OFFICER, ROLES.DRP, ROLES.BRP],
	},

	// Other Menus
	{
		path: "event/*",
		component: lazy(() => import("../pages/event/index")),
		allowedRoles: [
			ROLES.ADMIN,
			ROLES.STATE_OFFICER,
			ROLES.DRP,
			ROLES.BRP,
			ROLES.VRP,
		],
	},
	{
		path: "report/staff-registration",
		component: lazy(
			() => import("../pages/staffReport/StaffRegistrationReportPage"),
		),
		allowedRoles: [ROLES.ADMIN, ROLES.STATE_OFFICER, ROLES.DRP, ROLES.BRP],
	},
	{
		path: "report/vrp-registration",
		component: lazy(
			() => import("../pages/vrpRegistration/VrpRegistrationReportPage"),
		),
		allowedRoles: [ROLES.ADMIN, ROLES.STATE_OFFICER, ROLES.DRP, ROLES.BRP],
	},
	{
		path: "report/training",
		component: lazy(() => import("../pages/training/TrainingReportsPage")),
		allowedRoles: [ROLES.ADMIN, ROLES.STATE_OFFICER, ROLES.DRP, ROLES.BRP],
	},
	{
		path: "report/dsje-scheme",
		component: lazy(() => import("../pages/schemeReport/DsjeSchemeReportPage")),
		allowedRoles: [ROLES.ADMIN, ROLES.STATE_OFFICER, ROLES.DRP, ROLES.BRP],
	},
	{
		path: "report/scheme-data-entry",
		component: lazy(
			() => import("../pages/schemeDataEntryReport/SchemeDataEntryReportPage"),
		),
		allowedRoles: [ROLES.ADMIN, ROLES.STATE_OFFICER, ROLES.DRP, ROLES.BRP],
	},
	{
		path: "report/event",
		component: lazy(() => import("../pages/event/EventReportsPage")),
		allowedRoles: [ROLES.ADMIN, ROLES.STATE_OFFICER, ROLES.DRP, ROLES.BRP],
	},
	{
		path: "report/complaint",
		component: lazy(
			() => import("../pages/complaintReport/ComplaintReportPage"),
		),
		allowedRoles: [ROLES.ADMIN, ROLES.STATE_OFFICER, ROLES.DRP, ROLES.BRP],
	},
	{
		path: "video-conference",
		component: lazy(() => import("../pages/videoConference/index")),
		allowedRoles: [ROLES.ADMIN],
	},
	{
		path: "report/internal",
		component: lazy(() => import("../pages/misReport/InternalReportsPage")),
		allowedRoles: [ROLES.ADMIN],
	},
	{
		path: "report/internal/brp-performance",
		component: lazy(() => import("../pages/misReport/BrpPerformanceReportPage")),
		allowedRoles: [ROLES.ADMIN],
	},
	{
		path: "report/internal/vrp-engaged",
		component: lazy(() => import("../pages/misReport/VrpEngagedReportPage")),
		allowedRoles: [ROLES.ADMIN],
	},
	{
		path: "report/mis",
		component: lazy(() => import("../pages/misReport/MisReportsPage")),
		allowedRoles: [ROLES.ADMIN, ROLES.STATE_OFFICER, ROLES.DRP, ROLES.BRP],
	},
	{
		path: "report/mis/gram-panchayat-social-audit",
		component: lazy(
			() => import("../pages/misReport/GramPanchayatSocialAuditReportPage"),
		),
		allowedRoles: [ROLES.ADMIN, ROLES.STATE_OFFICER, ROLES.DRP, ROLES.BRP],
	},
	{
		path: "report/mis/individual-issue-listing",
		component: lazy(
			() => import("../pages/misReport/IndividualIssueListingReportPage"),
		),
		allowedRoles: [ROLES.ADMIN, ROLES.STATE_OFFICER, ROLES.DRP, ROLES.BRP],
	},
	{
		path: "report/mis/issues-by-category",
		component: lazy(
			() => import("../pages/misReport/IssuesByCategoryReportPage"),
		),
		allowedRoles: [ROLES.ADMIN, ROLES.STATE_OFFICER, ROLES.DRP, ROLES.BRP],
	},
	// {
	// 	path: "report/mis/issues-by-brp",
	// 	component: lazy(() => import("../pages/misReport/IssuesByBrpReportPage")),
	// 	allowedRoles: [ROLES.ADMIN, ROLES.STATE_OFFICER, ROLES.DRP, ROLES.BRP],
	// },
	{
		path: "report/mis/nrega-audit-report",
		component: lazy(() => import("../pages/misReport/NregaAuditReportPage")),
		allowedRoles: [ROLES.ADMIN, ROLES.STATE_OFFICER, ROLES.DRP, ROLES.BRP],
	},
	{
		path: "report/mis/drp-consolidated-annual-report",
		component: lazy(
			() => import("../pages/misReport/DrpConsolidatedAnnualReportPage"),
		),
		allowedRoles: [ROLES.ADMIN],
	},
	{
		path: "report/mis/brp-consolidated-annual-report",
		component: lazy(
			() => import("../pages/misReport/BrpConsolidatedAnnualReportPage"),
		),
		allowedRoles: [ROLES.ADMIN],
	},
	{
		path: "smars",
		component: lazy(() => import("../pages/smars/index")),
		allowedRoles: [ROLES.ADMIN],
	},
	{
		path: "performance-review",
		component: lazy(() => import("../pages/performanceReview/index")),
		allowedRoles: [ROLES.ADMIN, ROLES.DRP, ROLES.BRP],
	},
	{
		path: "our-locations",
		component: lazy(() => import("../pages/locations/index")),
		allowedRoles: [ROLES.ADMIN, ROLES.DRP, 6 /* DRP I/C */],
	},
	{
		path: "user-management",
		component: lazy(() => import("../pages/users/index")),
		allowedRoles: [ROLES.ADMIN],
	},
	{
		path: "user-management/add",
		component: lazy(() => import("../pages/users/UserFormPage")),
		allowedRoles: [ROLES.ADMIN],
	},
	{
		path: "user-management/edit/:id",
		component: lazy(() => import("../pages/users/UserFormPage")),
		allowedRoles: [ROLES.ADMIN],
	},
	{
		path: "user-management/view/:id",
		component: lazy(() => import("../pages/users/UserFormPage")),
		allowedRoles: [ROLES.ADMIN],
	},
	{
		path: "registration/vrp/*",
		component: lazy(() => import("../pages/vrpRegistration/index")),
		allowedRoles: [
			ROLES.ADMIN,
			ROLES.STATE_OFFICER,
			ROLES.DRP,
			ROLES.BRP,
			ROLES.VRP,
		],
	},
	{
		path: "training/*",
		component: lazy(() => import("../pages/training/index")),
		allowedRoles: [
			ROLES.ADMIN,
			ROLES.STATE_OFFICER,
			ROLES.DRP,
			ROLES.BRP,
			ROLES.VRP,
		],
	},
	// {
	// 	path: "registration/add",
	// 	component: lazy(
	// 		() => import("../pages/registration/components/RegistrationFormPage"),
	// 	),
	// 	allowedRoles: [ROLES.ADMIN, ROLES.STATE_OFFICER, ROLES.DRP, ROLES.BRP],
	// },
	{
		path: "change-password",
		component: lazy(() => import("../pages/users/ChangePasswordPage")),
		allowedRoles: [
			ROLES.ADMIN,
			ROLES.STATE_OFFICER,
			ROLES.DRP,
			ROLES.BRP,
			ROLES.VRP,
		],
	},
];

export default routesConfig;
